<template>
  <div class="portal-site-page">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <span>官网门户（文案配置）</span>
          <el-button class="btn-primary" :loading="saving" @click="saveConfig">保存配置</el-button>
        </div>
      </template>

      <p class="page-hint">
        配置会保存到服务器 <code>data/portal-site.json</code>；官网首页通过 <code>GET /api/portal-site-config</code> 拉取。
        首页轮播请在「轮播图管理」维护；本页负责 SEO、菜单文案、关于与联系、页脚等。
      </p>

      <el-alert
        type="info"
        :closable="false"
        show-icon
        title="首页轮播已统一到「轮播图管理」"
        description="官网首页轮播请前往「轮播图管理」上传，适用端选择「仅网页端」或「全部」。本页仅维护 SEO、菜单文案、关于与联系、页脚。"
        style="margin-bottom: 16px;"
      />

      <el-tabs v-model="activeTab">
        <el-tab-pane label="页面与 SEO" name="seo">
          <el-form label-width="140px" size="small" class="narrow-form">
            <el-form-item label="浏览器标题">
              <el-input v-model="form.page_title" maxlength="120" show-word-limit />
            </el-form-item>
            <el-form-item label="meta 描述">
              <el-input v-model="form.meta_description" type="textarea" :rows="2" maxlength="500" show-word-limit />
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="精选菜单区块" name="menu">
          <el-form label-width="120px" size="small" class="narrow-form">
            <el-form-item label="区块标题">
              <el-input v-model="form.menu_section_title" maxlength="80" />
            </el-form-item>
            <el-form-item label="导语">
              <el-input v-model="form.menu_section_lead" type="textarea" :rows="2" maxlength="300" show-word-limit />
            </el-form-item>
            <div class="subhead">三个卡片（可改文案）</div>
            <div v-for="(c, i) in form.menu_cards" :key="'m' + i" class="menu-card-row">
              <el-input v-model="c.title" placeholder="标题" class="mc-title" />
              <el-input v-model="c.text" type="textarea" :rows="2" placeholder="描述" class="mc-text" />
            </div>
            <el-button size="small" class="btn-secondary" @click="addMenuCard">增加卡片</el-button>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="关于与联系" name="about">
          <el-form label-width="120px" size="small" class="narrow-form">
            <el-form-item label="关于 · 标题">
              <el-input v-model="form.about_title" maxlength="80" />
            </el-form-item>
            <el-form-item label="关于 · 正文">
              <el-input v-model="form.about_body" type="textarea" :rows="5" maxlength="4000" show-word-limit />
            </el-form-item>
            <el-form-item label="联系 · 标题">
              <el-input v-model="form.contact_title" maxlength="80" />
            </el-form-item>
            <el-form-item label="联系 · 正文">
              <el-input v-model="form.contact_body" type="textarea" :rows="3" maxlength="2000" show-word-limit />
            </el-form-item>
            <div class="subhead">二维码区块</div>
            <el-form-item label="客服二维码图">
              <div class="img-row">
                <el-input v-model="form.contact_wechat_qr_image" placeholder="assets/images/... 或 /uploads/..." />
                <el-upload
                  action="#"
                  :show-file-list="false"
                  :http-request="(p) => uploadByField(p, 'contact_wechat_qr_image')"
                  accept="image/*"
                >
                  <el-button size="small" class="btn-secondary">上传</el-button>
                </el-upload>
              </div>
              <el-image
                v-if="form.contact_wechat_qr_image"
                :src="previewUrl(form.contact_wechat_qr_image)"
                fit="cover"
                class="qr-preview"
              />
            </el-form-item>
            <el-form-item label="客服二维码文案">
              <el-input v-model="form.contact_wechat_qr_caption" maxlength="30" />
            </el-form-item>
            <el-form-item label="小程序码图片">
              <div class="img-row">
                <el-input v-model="form.contact_mini_qr_image" placeholder="assets/images/... 或 /uploads/..." />
                <el-upload
                  action="#"
                  :show-file-list="false"
                  :http-request="(p) => uploadByField(p, 'contact_mini_qr_image')"
                  accept="image/*"
                >
                  <el-button size="small" class="btn-secondary">上传</el-button>
                </el-upload>
              </div>
              <el-image
                v-if="form.contact_mini_qr_image"
                :src="previewUrl(form.contact_mini_qr_image)"
                fit="cover"
                class="qr-preview"
              />
            </el-form-item>
            <el-form-item label="小程序码文案">
              <el-input v-model="form.contact_mini_qr_caption" maxlength="30" />
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="页脚" name="footer">
          <el-form label-width="120px" size="small" class="narrow-form">
            <el-form-item label="版权行">
              <el-input v-model="form.footer_copyright" maxlength="120" />
            </el-form-item>
            <el-form-item label="备案号">
              <el-input v-model="form.footer_icp" maxlength="120" />
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'

function emptySlide() {
  return {
    image: '',
    alt: '',
    title: '',
    paragraphs: [],
    _paragraphsText: ''
  }
}

export default {
  name: 'PortalSite',
  data() {
    return {
      activeTab: 'seo',
      saving: false,
      form: {
        page_title: '',
        meta_description: '',
        slides: [],
        menu_section_title: '',
        menu_section_lead: '',
        menu_cards: [],
        about_title: '',
        about_body: '',
        contact_title: '',
        contact_body: '',
        contact_wechat_qr_image: '',
        contact_wechat_qr_caption: '',
        contact_mini_qr_image: '',
        contact_mini_qr_caption: '',
        footer_copyright: '',
        footer_icp: ''
      }
    }
  },
  mounted() {
    this.loadConfig()
  },
  methods: {
    previewUrl(src) {
      const s = String(src || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s)) return s
      if (s.startsWith('/')) return s
      return '/' + s.replace(/^\/+/, '')
    },
    syncParagraphs(slide) {
      const t = String(slide._paragraphsText || '')
      slide.paragraphs = t
        .split(/\r?\n/)
        .map((x) => x.trim())
        .filter(Boolean)
    },
    decorateSlidesForForm(slides) {
      const list = Array.isArray(slides) ? slides : []
      return list.map((s) => {
        const paragraphs = Array.isArray(s.paragraphs) ? s.paragraphs : []
        return {
          image: s.image || '',
          alt: s.alt || '',
          title: s.title || '',
          paragraphs,
          _paragraphsText: paragraphs.join('\n')
        }
      })
    },
    async loadConfig() {
      try {
        const resp = await this.$api.get('/admin/portal-site')
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data
          this.form.page_title = d.page_title != null ? String(d.page_title) : ''
          this.form.meta_description = d.meta_description != null ? String(d.meta_description) : ''
          this.form.slides = this.decorateSlidesForForm(d.slides)
          this.form.menu_section_title = d.menu_section_title != null ? String(d.menu_section_title) : ''
          this.form.menu_section_lead = d.menu_section_lead != null ? String(d.menu_section_lead) : ''
          this.form.menu_cards = Array.isArray(d.menu_cards)
            ? d.menu_cards.map((c) => ({ title: c.title || '', text: c.text || '' }))
            : []
          this.form.about_title = d.about_title != null ? String(d.about_title) : ''
          this.form.about_body = d.about_body != null ? String(d.about_body) : ''
          this.form.contact_title = d.contact_title != null ? String(d.contact_title) : ''
          this.form.contact_body = d.contact_body != null ? String(d.contact_body) : ''
          this.form.contact_wechat_qr_image = d.contact_wechat_qr_image != null ? String(d.contact_wechat_qr_image) : ''
          this.form.contact_wechat_qr_caption =
            d.contact_wechat_qr_caption != null ? String(d.contact_wechat_qr_caption) : ''
          this.form.contact_mini_qr_image = d.contact_mini_qr_image != null ? String(d.contact_mini_qr_image) : ''
          this.form.contact_mini_qr_caption =
            d.contact_mini_qr_caption != null ? String(d.contact_mini_qr_caption) : ''
          this.form.footer_copyright = d.footer_copyright != null ? String(d.footer_copyright) : ''
          this.form.footer_icp = d.footer_icp != null ? String(d.footer_icp) : ''
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载官网配置失败'))
      }
    },
    async uploadSlide(param, idx) {
      if (!param || !param.file) return
      const url = await this.uploadCommon(param.file)
      if (url && this.form.slides[idx]) this.$set(this.form.slides[idx], 'image', url)
      if (param.onSuccess) param.onSuccess()
    },
    async uploadByField(param, field) {
      if (!param || !param.file) return
      const url = await this.uploadCommon(param.file)
      if (url && field) this.$set(this.form, field, url)
      if (param.onSuccess) param.onSuccess()
    },
    async uploadCommon(file) {
      const formData = new FormData()
      formData.append('file', file)
      try {
        const resp = await this.$api.post('/admin/portal-site/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          this.$message.success('上传成功')
          return resp.data.url
        } else {
          this.$message.error((resp && resp.message) || '上传失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '上传失败'))
      }
      return ''
    },
    addSlide() {
      if (this.form.slides.length >= 8) return
      this.form.slides = [...this.form.slides, emptySlide()]
    },
    removeSlide(idx) {
      this.form.slides.splice(idx, 1)
      this.form.slides = [...this.form.slides]
    },
    addMenuCard() {
      if (this.form.menu_cards.length >= 12) {
        this.$message.warning('最多 12 张卡片')
        return
      }
      this.form.menu_cards = [...this.form.menu_cards, { title: '', text: '' }]
    },
    buildPayload() {
      this.form.slides.forEach((s) => this.syncParagraphs(s))
      return {
        page_title: this.form.page_title,
        meta_description: this.form.meta_description,
        slides: this.form.slides.map((s) => ({
          image: s.image,
          alt: s.alt,
          title: s.title,
          paragraphs: (s.paragraphs || []).slice(0, 6)
        })),
        menu_section_title: this.form.menu_section_title,
        menu_section_lead: this.form.menu_section_lead,
        menu_cards: (this.form.menu_cards || []).filter((c) => String(c.title || '').trim()),
        about_title: this.form.about_title,
        about_body: this.form.about_body,
        contact_title: this.form.contact_title,
        contact_body: this.form.contact_body,
        contact_wechat_qr_image: this.form.contact_wechat_qr_image,
        contact_wechat_qr_caption: this.form.contact_wechat_qr_caption,
        contact_mini_qr_image: this.form.contact_mini_qr_image,
        contact_mini_qr_caption: this.form.contact_mini_qr_caption,
        footer_copyright: this.form.footer_copyright,
        footer_icp: this.form.footer_icp
      }
    },
    async saveConfig() {
      this.saving = true
      try {
        const payload = this.buildPayload()
        const resp = await this.$api.put('/admin/portal-site', payload)
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '保存成功')
          await this.loadConfig()
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.portal-site-page {
  padding: 0;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.page-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin: 0 0 16px;
}
.page-hint code {
  font-size: 11px;
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
}
.narrow-form {
  max-width: 720px;
}
.slides-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.toolbar-hint {
  font-size: 12px;
  color: #909399;
}
.slide-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px 16px 4px;
  margin-bottom: 12px;
  background: #fafafa;
}
.slide-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.danger-link {
  color: #f56c6c !important;
}
.img-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.img-row .el-input {
  flex: 1;
}
.slide-preview {
  width: 100%;
  max-width: 360px;
  height: 160px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.qr-preview {
  width: 100%;
  max-width: 120px;
  height: 120px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.subhead {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin: 12px 0 8px;
}
.menu-card-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px;
  margin-bottom: 8px;
  align-items: start;
}
.mc-title {
  width: 100%;
}
.mc-text {
  width: 100%;
}
</style>
